import { useEffect, useState } from "react";
import { Lock, Loader2 } from "lucide-react";
import Logo from "../components/Logo";
import { adminLogin, adminLogout, approveTestimonial, getSubmissions } from "../lib/api";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

function LoginCard({ onLogin }) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code || busy) return;
    setBusy(true);
    setError("");
    try {
      await adminLogin(code);
      await onLogin();
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid access code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-paper px-6">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md border border-line p-10 md:p-12"
      >
        <p className="eyebrow text-ember">Temper — Internal</p>
        <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight">Restricted.</h1>
        <p className="mt-3 text-sm leading-relaxed text-steel">
          Enter the access code to view contact submissions and TEMPER Profiles.
        </p>
        <label className="mt-10 block">
          <span className="eyebrow text-ink/50">Access code</span>
          <input
            data-testid="admin-code-input"
            type="password"
            className="field-input mt-2"
            placeholder="••••••••••••"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="current-password"
          />
        </label>
        {error && (
          <p data-testid="admin-login-error" className="mt-4 text-sm text-forge">
            {error}
          </p>
        )}
        <button type="submit" data-testid="admin-login-submit" disabled={busy} className="btn-ink mt-10 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
          Unlock submissions
        </button>
      </form>
    </div>
  );
}

function MiniBars({ scores }) {
  return (
    <div className="grid grid-cols-6 gap-2">
      {Object.entries(scores || {}).map(([word, v]) => (
        <div key={word}>
          <div className="h-1 w-full bg-ink/10">
            <div className="h-full bg-ember" style={{ width: `${v}%` }} />
          </div>
          <p className="mt-1.5 font-mono text-[9px] uppercase tracking-wider text-ink/50">
            {word.slice(0, 3)} {v}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function Admin() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("contacts");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await getSubmissions();
      setData(d);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const logout = async () => {
    await adminLogout();
    setData(null);
  };

  const setStatus = async (id, status) => {
    try {
      await approveTestimonial(id, status);
      await load();
    } catch (e) {
      console.error("Failed to update testimony", e);
    }
  };

  if (loading) {
    return (
      <div className="grid min-h-screen place-items-center bg-paper">
        <Loader2 className="h-6 w-6 animate-spin text-ember" />
      </div>
    );
  }

  if (!data) return <LoginCard onLogin={load} />;

  const contacts = data.contacts || [];
  const profiles = data.questionnaires || [];

  return (
    <div className="min-h-screen bg-paper pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-[1280px] px-6 md:px-10">
        <header className="flex flex-wrap items-end justify-between gap-6 border-b border-line pb-8">
          <div>
            <Logo />
            <h1 className="mt-4 font-display text-4xl font-extrabold tracking-tight md:text-5xl" data-testid="admin-heading">
              Submissions
            </h1>
            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-ink/50">
              {contacts.length} conversations · {profiles.length} profiles
            </p>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="btn-outline">
            Log out
          </button>
        </header>

        <div className="mt-8 flex gap-8 border-b border-line">
          {[
            ["contacts", `Conversations (${contacts.length})`],
            ["callRequests", `Call Requests (${(data.call_requests || []).length})`],
            ["profiles", `Temper Profiles (${profiles.length})`],
            ["testimonials", `Testimonials (${(data.testimonials || []).length})`],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`-mb-px border-b-2 pb-4 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                tab === key ? "border-ember text-ink" : "border-transparent text-ink/50 hover:text-ink"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "callRequests" ? (
          <div data-testid="admin-callrequests-list" className="divide-y divide-line">
            {(data.call_requests || []).length === 0 && (
              <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.2em] text-ink/40">
                No call requests yet
              </p>
            )}
            {(data.call_requests || []).map((r) => (
              <article key={r.id} data-testid="admin-callrequest-row" className="grid gap-6 py-8 md:grid-cols-12 md:items-center">
                <div className="md:col-span-4">
                  <h3 className="font-display text-xl font-bold tracking-tight">{r.name}</h3>
                  <p className="mt-1 font-mono text-xs text-steel">
                    {r.email}
                    {r.organization ? ` · ${r.organization}` : ""}
                  </p>
                </div>
                <div className="md:col-span-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/40">Call</p>
                  <p className="mt-1 text-sm text-ink/80">{r.call_type}</p>
                </div>
                <div className="md:col-span-3">
                  <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/40">Requested</p>
                  <p className="mt-1 text-sm text-ink/80">
                    {r.date && r.time ? `${r.date} · ${r.time}` : "Eric will schedule by email"}
                  </p>
                </div>
                <div className="md:col-span-2 md:text-right">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/40">
                    {fmt(r.created_at)}
                  </span>
                </div>
                {r.notes && (
                  <p className="max-w-3xl text-sm leading-relaxed text-steel md:col-span-12">
                    “{r.notes}”
                  </p>
                )}
              </article>
            ))}
          </div>
        ) : tab === "testimonials" ? (
          <div data-testid="admin-testimonials-list" className="divide-y divide-line">
            {(data.testimonials || []).length === 0 && (
              <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.2em] text-ink/40">
                No testimonies yet
              </p>
            )}
            {(data.testimonials || []).map((t) => (
              <article key={t.id} data-testid="admin-testimonial-row" className="grid gap-6 py-8 md:grid-cols-12 md:items-center">
                <div className="md:col-span-4">
                  <h3 className="font-display text-xl font-bold tracking-tight">{t.name}</h3>
                  <p className="mt-1 font-mono text-xs text-steel">
                    {[t.role, t.organization].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <div className="md:col-span-5">
                  <p className="leading-relaxed text-ink/80">“{t.testimony}”</p>
                </div>
                <div className="flex items-center gap-3 md:col-span-3 md:justify-end">
                  {t.status === "pending" ? (
                    <>
                      <button
                        data-testid={`testimonial-approve-${t.id}`}
                        onClick={() => setStatus(t.id, "approved")}
                        className="bg-ember px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-paper transition-colors duration-300 hover:bg-ink"
                      >
                        Approve
                      </button>
                      <button
                        data-testid={`testimonial-reject-${t.id}`}
                        onClick={() => setStatus(t.id, "rejected")}
                        className="border border-ink/25 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-300 hover:border-ink"
                      >
                        Reject
                      </button>
                    </>
                  ) : (
                    <span
                      className={`font-mono text-[10px] uppercase tracking-[0.18em] ${
                        t.status === "approved" ? "text-ember" : "text-ink/40"
                      }`}
                    >
                      {t.status}
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>
        ) : tab === "contacts" ? (
          <div data-testid="admin-contacts-list" className="divide-y divide-line">
            {contacts.length === 0 && (
              <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.2em] text-ink/40">
                No conversations yet
              </p>
            )}
            {contacts.map((c) => (
              <article key={c.id} data-testid="admin-contact-row" className="py-8">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <h3 className="font-display text-xl font-bold tracking-tight">{c.name}</h3>
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/40">
                    {fmt(c.created_at)}
                  </span>
                </div>
                <p className="mt-1 font-mono text-xs text-steel">
                  {c.email}
                  {c.organization ? ` · ${c.organization}` : ""}
                  {c.interest ? ` · ${c.interest}` : ""}
                </p>
                <p className="mt-4 max-w-3xl leading-relaxed text-ink/80">{c.message}</p>
              </article>
            ))}
          </div>
        ) : (
          <div data-testid="admin-profiles-list" className="divide-y divide-line">
            {profiles.length === 0 && (
              <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.2em] text-ink/40">
                No profiles yet
              </p>
            )}
            {profiles.map((p) => (
              <article key={p.id} data-testid="admin-profile-row" className="grid gap-6 py-8 md:grid-cols-12 md:items-center">
                <div className="md:col-span-4">
                  <h3 className="font-display text-xl font-bold tracking-tight">{p.name}</h3>
                  <p className="mt-1 font-mono text-xs text-steel">
                    {p.email}
                    {p.role ? ` · ${p.role}` : ""}
                  </p>
                  {p.organization && (
                    <p className="font-mono text-xs text-steel">{p.organization}</p>
                  )}
                </div>
                <div className="md:col-span-2">
                  <span className="font-display text-3xl font-black text-ember">{p.overall}</span>
                  <span className="ml-1 font-mono text-[10px] text-ink/40">/100</span>
                </div>
                <div className="md:col-span-5">
                  <MiniBars scores={p.scores} />
                </div>
                <div className="md:col-span-1 md:text-right">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink/40">
                    {fmt(p.created_at)}
                  </span>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
